import {
   ChangeEvent,
   FC,
   useCallback,
   useEffect,
   useMemo,
   useState,
} from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import AuthLayout from "@/components/AuthLayout"
import Button from "@/components/shared/Button"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import Link from "@/components/shared/Link"
import TextInput from "@/components/shared/TextInput"
import { HttpCodes } from "@/constants/globals"
import { PageRoutes } from "@/constants/routes"
import useQueryParams from "@/hooks/useQueryParams"
import useStateHandler from "@/hooks/useStateHandler"
import { AppDispatch, RootState } from "@/store"
import {
   verify,
   activateUser,
   passwordRecoveryConfirm,
} from "@/store/authSlice"
import { QueryParameter, Variant } from "@/types/index"
import { isNull } from "@/utils/lodash"
import "./index.scss"

enum ErrorType {
   API_ERROR = "api_error",
   MISMATCH = "mismatch",
}

const NewPassword: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const {
      loadingVerification,
      errorVerification,
      loadingUserActivation,
      errorUserActivation,
   } = useSelector(({ authReducer }: RootState) => authReducer)
   const navigate = useNavigate()
   const { getQueryParamByKey } = useQueryParams()
   const activationToken = getQueryParamByKey(QueryParameter.ACTIVATION) || null
   const recoveryToken = getQueryParamByKey(QueryParameter.RECOVERY) || null
   const [errorType, setErrorType] = useState<ErrorType>()

   const [requirements, setRequirements] = useState({
      minLength: false,
      upperCase: false,
      lowerCase: false,
      number: false,
      specialChar: false,
   })
   const [state, setState] = useStateHandler({
      password: "",
      confirmPassword: "",
   })
   const { password, confirmPassword } = state

   const handleVerificationToken = useCallback(async () => {
      try {
         if (activationToken === null && recoveryToken === null) {
            return
         }
         const token =
            activationToken !== null ? activationToken : recoveryToken!
         await dispatch(
            verify({
               token,
            })
         ).unwrap()
      } catch (error) {
         setErrorType(ErrorType.API_ERROR)
         console.error("Error verifying token:", error)
      }
   }, [activationToken, dispatch, recoveryToken])

   const handleUserActivation = useCallback(async () => {
      try {
         if (activationToken === null) {
            return
         }
         const result = await dispatch(
            activateUser({
               token: activationToken,
               password,
               confirmPassword,
            })
         ).unwrap()
         if (result.status === 200) {
            navigate(PageRoutes.LOGIN)
         }
      } catch (error) {
         setErrorType(ErrorType.API_ERROR)
         console.error("Error activation user:", error)
      }
   }, [activationToken, confirmPassword, dispatch, navigate, password])

   const handlePasswordRecoveryConfirmation = useCallback(async () => {
      try {
         if (recoveryToken === null) {
            return
         }
         const result = await dispatch(
            passwordRecoveryConfirm({
               token: recoveryToken,
               password,
               confirmPassword,
            })
         ).unwrap()
         if (result.status === 200) {
            navigate(PageRoutes.LOGIN)
         }
      } catch (error) {
         setErrorType(ErrorType.API_ERROR)
         console.error("Error password recovery confirmation:", error)
      }
   }, [confirmPassword, dispatch, navigate, password, recoveryToken])

   const areRequirementsMet = useMemo(() => {
      return Object.values(requirements).every(Boolean)
   }, [requirements])

   const arePasswordsMatch = useMemo(() => {
      return password === confirmPassword
   }, [password, confirmPassword])

   const isTokenVerified = useMemo(
      () => !loadingVerification && !errorType,
      [loadingVerification, errorType]
   )

   const isFormValid = useMemo(() => {
      return isTokenVerified && areRequirementsMet && arePasswordsMatch
   }, [isTokenVerified, areRequirementsMet, arePasswordsMatch])

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setState(e)
      if (errorType) {
         setErrorType(undefined)
      }

      if (e.target.name === "password") {
         const value = e.target.value
         setRequirements({
            minLength: value.length >= 8,
            upperCase: /[A-Z]/.test(value),
            lowerCase: /[a-z]/.test(value),
            number: /\d/.test(value),
            specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
         })
      }
   }

   const getAPIErrorMessage = useCallback(
      (error: number) => {
         switch (error) {
            case HttpCodes.NOT_FOUND:
               return errorVerification ? (
                  <>
                     Your password reset link has expired. <br /> Please request
                     a new reset link if you still need to change your password.
                  </>
               ) : (
                  <>
                     Password confirmation incorrect, <br />
                     please review your password
                  </>
               )
            case HttpCodes.BAD_REQUEST:
               return "Incorrect information, please check and try again"
            default:
               return "An error occurred"
         }
      },
      [errorVerification]
   )

   const getErrorMessage = useCallback(() => {
      switch (errorType) {
         case ErrorType.API_ERROR: {
            const error =
               errorVerification !== null
                  ? errorVerification
                  : errorUserActivation!
            if (error === null) {
               return
            }
            return getAPIErrorMessage(error)
         }
         case ErrorType.MISMATCH:
            return "Passwords do not match"
         default:
            return "An error occurred"
      }
   }, [errorType, errorVerification, errorUserActivation, getAPIErrorMessage])

   const getRequirementClassName = (isMet: boolean) => {
      return classNames("new-password__instructions-description-requirement", {
         "new-password__instructions-description-requirement--valid": isMet,
         "new-password__instructions-description-requirement--invalid": !isMet,
      })
   }

   const requirementItem = (text: string, key: keyof typeof requirements) => {
      const iconProps: { name: IconProps["name"]; color: IconColor } =
         requirements[key]
            ? {
                 name: "checkmark",
                 color: IconColor.WHITE,
              }
            : {
                 name: "cross",
                 color: IconColor.HONOLULU_BLUE_LIGHT,
              }

      return (
         <li className={getRequirementClassName(requirements[key])}>
            <span>
               <Icon
                  {...iconProps}
                  size="12"
               />
            </span>
            <span>{text}</span>
         </li>
      )
   }

   const handleConfirm = useCallback(async () => {
      if (activationToken) {
         handleUserActivation()
      }
      if (recoveryToken) {
         handlePasswordRecoveryConfirmation()
      }
   }, [
      activationToken,
      handlePasswordRecoveryConfirmation,
      handleUserActivation,
      recoveryToken,
   ])

   useEffect(() => {
      handleVerificationToken()
   }, [handleVerificationToken])

   return (
      <AuthLayout
         iconName="shield"
         title="Create password"
         instructions={
            <>
               <div className="new-password__instructions-description">
                  <span>Password requirements</span>
                  <ul className="new-password__instructions-description-requirements">
                     {requirementItem("Minimum 8 characters", "minLength")}
                     {requirementItem(
                        "At least 1 upper case letter",
                        "upperCase"
                     )}
                     {requirementItem(
                        "At least 1 lower case letter",
                        "lowerCase"
                     )}
                     {requirementItem("At least 1 number", "number")}
                     {requirementItem(
                        "At least 1 special character",
                        "specialChar"
                     )}
                  </ul>
               </div>
            </>
         }
      >
         <>
            <TextInput
               name="password"
               variant={Variant.LARGE}
               type="password"
               disabled={
                  loadingVerification ||
                  loadingUserActivation ||
                  !isNull(errorVerification)
               }
               placeholder="Your new password"
               value={password}
               onChange={handleChange}
            />
            <TextInput
               name="confirmPassword"
               variant={Variant.LARGE}
               type="password"
               disabled={
                  loadingVerification ||
                  loadingUserActivation ||
                  !isNull(errorVerification)
               }
               placeholder="Confirm password"
               value={confirmPassword}
               onChange={handleChange}
               onBlur={() => {
                  if (!arePasswordsMatch) {
                     setErrorType(ErrorType.MISMATCH)
                  }
               }}
            />
            <Button
               disabled={!isFormValid}
               onClick={handleConfirm}
            >
               Confirm
            </Button>
            <Link
               to={PageRoutes.LOGIN}
               className="new-password__link"
            >
               Back to login screen
            </Link>
            <div className="new-password__error">
               {errorType && getErrorMessage()}
            </div>
         </>
      </AuthLayout>
   )
}

export default NewPassword
